import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    Baby, CalendarDays, ChevronDown, ChevronRight, HeartPulse, History, Home, Laptop, Leaf, Menu,
    MonitorSmartphone, ShoppingBasket, Sparkles, Tag, TicketPercent, UserRound, X,
} from "lucide-react";
import "./HomeSecondaryNavbar.css";
import BrowsingHistoryTimeline from "../BrowsingHistoryTimeline/BrowsingHistoryTimeline";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { fetchSuperCategories } from "../../../Redux/filters/slice";
import { RootState } from "../../../store";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

type HomeSecondaryNavbarProps = {
    browsingHistoryOpen: boolean;
    onBrowsingHistoryClick: () => void;
    variant?: "home" | "detail";
    activeSuperCategory?: string;
};

interface ParentCategory {
    _id: string;
    name: string;
    image?: string;
}

interface SubCategoryItem {
    _id: string;
    name: string;
}

interface CategoryMenuData {
    parents: ParentCategory[];
    subs: Record<string, SubCategoryItem[]>;
}

type DropdownSection = {
    title: string;
    items: string[];
};

type NavItem = {
    id: string;
    label: string;
    icon: React.ReactNode;
    dropdown?: DropdownSection[];
    featured?: boolean;
    highlight?: boolean;
    isDynamic?: boolean;
};

const getCategoryIcon = (name: string): React.ReactNode => {
    const lower = name.toLowerCase();
    if (lower.includes("men") && !lower.includes("women")) return <UserRound size={15} strokeWidth={1.9} />;
    if (lower.includes("women")) return <HeartPulse size={15} strokeWidth={1.9} />;
    if (lower.includes("fresh") || lower.includes("vegetable") || lower.includes("fruit")) return <Leaf size={15} strokeWidth={1.9} />;
    if (lower.includes("grocer") || lower.includes("food") || lower.includes("staple")) return <ShoppingBasket size={15} strokeWidth={1.9} />;
    if (lower.includes("kid") || lower.includes("toy") || lower.includes("baby")) return <Baby size={15} strokeWidth={1.9} />;
    if (lower.includes("beaut") || lower.includes("makeup") || lower.includes("cosmetic")) return <Sparkles size={15} strokeWidth={1.9} />;
    if (lower.includes("mobile") || lower.includes("phone")) return <MonitorSmartphone size={15} strokeWidth={1.9} />;
    if (lower.includes("electron") || lower.includes("computer") || lower.includes("gadget")) return <Laptop size={15} strokeWidth={1.9} />;
    if (lower.includes("home") || lower.includes("kitchen") || lower.includes("appliance")) return <Home size={15} strokeWidth={1.9} />;
    return <Sparkles size={15} strokeWidth={1.9} />;
};

const defaultNavItems: NavItem[] = [
    {
        id: "men",
        label: "Men",
        icon: <UserRound size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Top Wear",
                items: ["T-Shirts", "Casual Shirts", "Formal Shirts", "Sweatshirts", "Sweaters", "Jackets", "Blazers & Coats", "Suits", "Rain Jackets"],
            },
            {
                title: "Bottomwear",
                items: ["Jeans", "Casual Trousers", "Formal Trousers", "Shorts", "Track Pants & Joggers"],
            },
            {
                title: "Footwear",
                items: ["Casual Shoes", "Sports Shoes", "Formal Shoes", "Sneakers", "Sandals & Floaters", "Flip Flops", "Socks"],
            },
            {
                title: "Sports & Active Wear",
                items: ["Sports Shoes", "Sports Sandals", "Active T-Shirts", "Track Pants & Shorts", "Tracksuits", "Jackets & Sweatshirts", "Sports Accessories", "Swimwear"],
            },
            {
                title: "Fashion Accessories",
                items: ["Wallets", "Belts", "Perfumes & Body Mists", "Trimmers", "Deodorants", "Ties, Cufflinks & Pocket Squares", "Accessory Gift Sets", "Caps & Hats", "Mufflers, Scarves & Gloves", "Phone Cases", "Rings & Wristwear", "Helmets"],
            },
        ],
    },
    {
        id: "women",
        label: "Women",
        icon: <HeartPulse size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Indian & Fusion Wear",
                items: ["Kurtas & Suits", "Kurtis", "Sarees", "Ethnic Wear", "Leggings & Churidars", "Dress Materials", "Lehenga Cholis", "Dupattas & Shawls"],
            },
            {
                title: "Western Wear",
                items: ["Dresses", "Tops", "T-Shirts", "Jeans", "Trousers & Capris", "Shorts & Skirts", "Jumpsuits", "Jackets & Coats"],
            },
            {
                title: "Footwear",
                items: ["Flats", "Casual Shoes", "Heels", "Boots", "Sports Shoes", "Sandals"],
            },
            {
                title: "Beauty & Personal",
                items: ["Makeup", "Skincare", "Premium Beauty", "Lipsticks", "Fragrances", "Haircare"],
            },
            {
                title: "Jewellery & Accessories",
                items: ["Fashion Jewellery", "Fine Jewellery", "Earrings", "Handbags", "Clutches", "Belts", "Watches", "Sunglasses"],
            },
        ],
    },
    {
        id: "fresh",
        label: "Fresh",
        icon: <Leaf size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Fresh Produce",
                items: ["Fresh Vegetables", "Fresh Fruits", "Leafy Vegetables", "Herbs", "Organic Produce", "Exotic Fruits"],
            },
            {
                title: "Daily Fresh",
                items: ["Milk", "Curd", "Paneer", "Eggs", "Bread", "Butter", "Cheese"],
            },
            {
                title: "Meat & Seafood",
                items: ["Chicken", "Mutton", "Fish", "Prawns", "Ready to Cook"],
            },
        ],
    },
    {
        id: "groceries",
        label: "Groceries",
        icon: <ShoppingBasket size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Staples",
                items: ["Atta & Flour", "Rice", "Pulses", "Dal", "Cooking Oil", "Salt & Sugar", "Spices"],
            },
            {
                title: "Snacks & Beverages",
                items: ["Biscuits", "Chips", "Namkeen", "Tea", "Coffee", "Soft Drinks", "Juices"],
            },
            {
                title: "Breakfast",
                items: ["Cereals", "Oats", "Muesli", "Spreads", "Honey", "Instant Breakfast"],
            },
            {
                title: "Household",
                items: ["Cleaning Supplies", "Laundry", "Dishwash", "Air Fresheners", "Kitchen Essentials"],
            },
        ],
    },
    {
        id: "kids",
        label: "Kids & Toys",
        icon: <Baby size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Kids Fashion",
                items: ["Boys Clothing", "Girls Clothing", "Infant Wear", "Kids Footwear", "School Wear"],
            },
            {
                title: "Toys",
                items: ["Remote Control Toys", "Action Figures", "Soft Toys", "Learning Toys", "Board Games", "Outdoor Toys"],
            },
            {
                title: "Baby Care",
                items: ["Diapers", "Baby Food", "Baby Skincare", "Feeding", "Strollers", "Nursery"],
            },
        ],
    },
    {
        id: "beauty",
        label: "Beauty & Personal",
        icon: <Sparkles size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Makeup",
                items: ["Face", "Eyes", "Lips", "Nails", "Makeup Kits"],
            },
            {
                title: "Skincare",
                items: ["Face Wash", "Moisturisers", "Serums", "Sunscreen", "Masks", "Body Care"],
            },
            {
                title: "Hair Care",
                items: ["Shampoo", "Conditioner", "Hair Serum", "Hair Oil", "Styling"],
            },
            {
                title: "Fragrances",
                items: ["Perfumes", "Deodorants", "Body Mists", "Luxury Fragrances"],
            },
        ],
    },
    {
        id: "mobiles",
        label: "Mobiles",
        icon: <MonitorSmartphone size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Smartphones",
                items: ["iPhone", "Samsung", "OnePlus", "Google Pixel", "Xiaomi", "Motorola", "Realme"],
            },
            {
                title: "Accessories",
                items: ["Cases & Covers", "Screen Protectors", "Chargers", "Power Banks", "Cables", "Mobile Holders"],
            },
        ],
    },
    {
        id: "electronics",
        label: "Electronics",
        icon: <Laptop size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Computers",
                items: ["Laptops", "Desktops", "Monitors", "Keyboards", "Mouse", "Storage"],
            },
            {
                title: "Audio",
                items: ["Headphones", "Earbuds", "Speakers", "Soundbars", "Microphones"],
            },
            {
                title: "Wearables",
                items: ["Smart Watches", "Fitness Bands", "Smart Rings", "Smart Glasses"],
            },
            {
                title: "Gaming",
                items: ["Gaming Consoles", "Controllers", "Gaming Laptops", "Gaming Accessories"],
            },
        ],
    },
    {
        id: "home",
        label: "Home & Kitchen Appliances",
        icon: <Home size={15} strokeWidth={1.9} />,
        dropdown: [
            {
                title: "Kitchen Appliances",
                items: ["Mixer Grinders", "Microwave Ovens", "Air Fryers", "Coffee Makers", "Induction Cooktops", "Juicers"],
            },
            {
                title: "Home Appliances",
                items: ["Air Conditioners", "Refrigerators", "Washing Machines", "Vacuum Cleaners", "Water Purifiers"],
            },
            {
                title: "Home & Living",
                items: ["Bedsheets", "Curtains", "Lighting", "Home Decor", "Storage", "Furniture"],
            },
        ],
    },
];

const HomeSecondaryNavbar = ({
    browsingHistoryOpen,
    onBrowsingHistoryClick,
    variant = "home",
    activeSuperCategory,
}: HomeSecondaryNavbarProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { superCategories } = useAppSelector((state: RootState) => state.filters);

    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [menuCache, setMenuCache] = useState<Record<string, CategoryMenuData>>({});
    const [loadingMenu, setLoadingMenu] = useState(false);
    const navRef = useRef<HTMLDivElement>(null);

    // Fetch superCategories if not yet loaded (same as CategoriesBar)
    useEffect(() => {
        if (superCategories.data.length === 0 && !superCategories.loading) {
            dispatch(
                fetchSuperCategories({
                    categoryTypeName: "Ecommerce",
                    showOnWebsite: true,
                    showOnHomepage: true,
                })
            );
        }
    }, [dispatch, superCategories.data.length, superCategories.loading]);

    // Outside click handler to close dropdown
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (navRef.current && !navRef.current.contains(target)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, []);

    // Construct dynamic navItems list - sliced to fit neatly on screen
    const navItems = useMemo<NavItem[]>(() => {
        const maxCategories = 7;
        const hasData = Boolean(superCategories.data && superCategories.data.length > 0);
        const sourceData = hasData ? superCategories.data : [];
        const visibleData = hasData
            ? sourceData.slice(0, maxCategories)
            : defaultNavItems.slice(0, maxCategories);
        const overflowData = hasData
            ? sourceData.slice(maxCategories)
            : defaultNavItems.slice(maxCategories);

        const dynamicCategories: NavItem[] = visibleData.map((cat: any) => ({
            id: cat.name ?? cat.id ?? cat._id,
            label: cat.label ?? cat.name ?? "",
            icon: cat.icon ?? getCategoryIcon(cat.name ?? ""),
            dropdown: cat.dropdown,
            isDynamic: true,
        }));

        // More categories dropdown if categories exceed maxCategories
        const moreItem: NavItem[] =
            overflowData.length > 0
                ? [
                      {
                          id: "more-categories",
                          label: "More",
                          icon: <ChevronDown size={14} strokeWidth={2} />,
                          dropdown: [
                              {
                                  title: "More Categories",
                                  items: overflowData.map((c: any) => c.name ?? c.label ?? ""),
                              },
                          ],
                      },
                  ]
                : [];

        const actionItems: NavItem[] = [
            {
                id: "history",
                label: "Browsing History",
                icon: <History size={15} strokeWidth={1.9} />,
            },
            {
                id: "deals",
                label: "Today's Deals",
                icon: <Tag size={15} strokeWidth={1.9} />,
                featured: true,
            },
            {
                id: "vouchers",
                label: "Shopping Vouchers",
                icon: <TicketPercent size={15} strokeWidth={1.9} />,
                highlight: true,
            },
            {
                id: "appointment",
                label: "Book an Appointment",
                icon: <CalendarDays size={15} strokeWidth={1.9} />,
            },
        ];

        return [...dynamicCategories, ...moreItem, ...actionItems];
    }, [superCategories.data]);

    // Load category parents & sub-categories dynamically (same as CategoriesBar)
    const loadCategoryMenu = async (categoryName: string) => {
        if (categoryName === "More" || menuCache[categoryName]) return;

        setLoadingMenu(true);
        try {
            const parentsRes = await axios.get(`${PRODUCT_BASE_URL}/categories/parent-categories`, {
                params: { superCategory: categoryName },
            });
            const parents: ParentCategory[] = parentsRes.data.data || [];

            // Fetch all sub-categories simultaneously
            const subResults = await Promise.all(
                parents.map((p) =>
                    axios
                        .get(`${PRODUCT_BASE_URL}/categories/sub-categories`, {
                            params: { parentCategory: p.name },
                        })
                        .catch(() => ({ data: { data: [] } }))
                )
            );

            const subs: Record<string, SubCategoryItem[]> = {};
            parents.forEach((p, i) => {
                subs[p.name] = subResults[i].data?.data || [];
            });

            setMenuCache((prev) => ({ ...prev, [categoryName]: { parents, subs } }));
        } catch (err) {
            console.error("Failed to load category menu:", err);
            setMenuCache((prev) => ({ ...prev, [categoryName]: { parents: [], subs: {} } }));
        }
        setLoadingMenu(false);
    };

    const handleItemClick = (item: NavItem) => {
        if (item.id === "history") {
            setActiveDropdown(null);
            setMobileOpen(false);
            onBrowsingHistoryClick?.();
            return;
        }
        if (item.id === "deals") {
            setActiveDropdown(null);
            setMobileOpen(false);
            navigate("/todays-deals");
            return;
        }
        if (item.id === "vouchers") {
            setActiveDropdown(null);
            setMobileOpen(false);
            navigate("/RefProducts");
            return;
        }
        if (item.id === "appointment") {
            setActiveDropdown(null);
            setMobileOpen(false);
            navigate("/BookAppointment");
            return;
        }

        // Category dropdown toggle
        if (activeDropdown === item.id) {
            setActiveDropdown(null);
        } else {
            setActiveDropdown(item.id);
            if (item.id !== "more-categories") {
                loadCategoryMenu(item.label);
            }
        }
    };

    const handleViewAll = (categoryLabel: string) => {
        setActiveDropdown(null);
        setMobileOpen(false);
        if (categoryLabel === "More" || categoryLabel === "All") {
            navigate("/Categories/All");
        } else {
            navigate(`/Categories/${encodeURIComponent(categoryLabel)}`);
        }
    };

    const handleParentClick = (categoryLabel: string, parentName: string) => {
        setActiveDropdown(null);
        setMobileOpen(false);
        navigate(`/Categories/${encodeURIComponent(categoryLabel)}?category=${encodeURIComponent(parentName)}`);
    };

    const handleSubClick = (categoryLabel: string, parentName: string, subName: string) => {
        setActiveDropdown(null);
        setMobileOpen(false);
        navigate(
            `/Categories/${encodeURIComponent(categoryLabel)}?category=${encodeURIComponent(
                parentName
            )}&subCategory=${encodeURIComponent(subName)}`
        );
    };

    const activeItem = navItems.find((item) => item.id === activeDropdown);
    const activeData = activeItem ? menuCache[activeItem.label] : null;
    const hasDynamicParents = Boolean(activeData && activeData.parents && activeData.parents.length > 0);
    const hasFallbackDropdown = Boolean(activeItem?.dropdown && activeItem.dropdown.length > 0);
    const hasDropdown =
        hasDynamicParents ||
        hasFallbackDropdown ||
        Boolean(
            loadingMenu &&
            activeItem &&
            activeItem.id !== "history" &&
            activeItem.id !== "deals" &&
            activeItem.id !== "vouchers" &&
            activeItem.id !== "appointment" &&
            activeItem.id !== "more-categories"
        );

    const activeColumnCount = hasDynamicParents
        ? activeData?.parents.length || 4
        : activeItem?.dropdown?.length || 4;

    return (
        <div className={`home-secondary-nav home-secondary-nav-${variant}`} ref={navRef}>
            <div className="home-secondary-nav-inner">
                <div className="home-secondary-nav-scroll">
                    {navItems.map((item) => {
                        const isHistoryActive = item.id === "history" && browsingHistoryOpen;
                        const isSuperActive = Boolean(
                            activeSuperCategory &&
                            activeSuperCategory.toUpperCase() === item.label.toUpperCase()
                        );
                        const isActive = activeDropdown === item.id || isHistoryActive || isSuperActive;
                        const itemHasDropdown =
                            item.id !== "history" &&
                            item.id !== "deals" &&
                            item.id !== "vouchers" &&
                            item.id !== "appointment";

                        return (
                            <button
                                type="button"
                                key={item.id}
                                className={[
                                    "home-secondary-nav-item",
                                    isActive ? "active" : "",
                                    isHistoryActive ? "history-active" : "",
                                    item.featured ? "featured" : "",
                                    item.highlight ? "highlight" : "",
                                ].join(" ")}
                                onClick={() => handleItemClick(item)}
                            >
                                <span className="home-secondary-nav-icon">{item.icon}</span>
                                <span className="home-secondary-nav-label">{item.label}</span>
                                {(itemHasDropdown || item.id === "history") && (
                                    <ChevronDown
                                        size={13}
                                        strokeWidth={2}
                                        className={
                                            item.id === "history"
                                                ? browsingHistoryOpen
                                                    ? "rotate"
                                                    : ""
                                                : activeDropdown === item.id
                                                ? "rotate"
                                                : ""
                                        }
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    className="home-secondary-mobile-trigger"
                    onClick={() => {
                        setMobileOpen((prev) => !prev);
                        setActiveDropdown(null);
                    }}
                >
                    {mobileOpen ? <X size={18} /> : <Menu size={18} />} <span>Categories</span>
                </button>
            </div>

            {/* Mega Dropdown */}
            {activeItem && hasDropdown && (
                <div className="home-secondary-mega">
                    <div className="home-secondary-mega-top">
                        <div>
                            <span className="home-secondary-mega-eyebrow">
                                {activeItem.id === "more-categories" ? "MORE CATEGORIES" : "EXPLORE"}
                            </span>
                            <h3>{activeItem.id === "more-categories" ? "All Categories" : activeItem.label}</h3>
                        </div>
                        <button
                            type="button"
                            className="home-secondary-view-all"
                            onClick={() => handleViewAll(activeItem.label)}
                        >
                            {activeItem.id === "more-categories" ? "View all categories" : `View all ${activeItem.label}`}{" "}
                            <ChevronRight size={14} />
                        </button>
                    </div>

                    {loadingMenu && !activeData && !activeItem.dropdown ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                padding: "36px 0",
                                gap: "10px",
                                color: "#173c72",
                            }}
                        >
                            <div
                                className="spinner-border spinner-border-sm"
                                role="status"
                                style={{ width: "22px", height: "22px", borderWidth: "2.5px" }}
                            />
                            <span style={{ fontSize: "11px", fontWeight: 600, color: "#687587" }}>
                                Loading categories…
                            </span>
                        </div>
                    ) : hasDynamicParents ? (
                        <div
                            className="home-secondary-mega-grid"
                            style={{
                                gridTemplateColumns: `repeat(${Math.min(activeColumnCount, 5)}, minmax(0, 1fr))`,
                            }}
                        >
                            {activeData?.parents.map((parent) => {
                                const subs = activeData.subs[parent.name] || [];
                                return (
                                    <div key={parent._id} className="home-secondary-mega-column">
                                        <h4
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleParentClick(activeItem.label, parent.name)}
                                        >
                                            {parent.name}
                                        </h4>
                                        <div className="home-secondary-mega-links">
                                            {subs.map((sub) => (
                                                <button
                                                    type="button"
                                                    key={sub._id}
                                                    onClick={() =>
                                                        handleSubClick(activeItem.label, parent.name, sub.name)
                                                    }
                                                >
                                                    <span>{sub.name}</span>
                                                    <ChevronRight size={12} />
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : activeItem.dropdown ? (
                        <div
                            className="home-secondary-mega-grid"
                            style={{
                                gridTemplateColumns: `repeat(${Math.min(activeItem.dropdown.length, 5)}, minmax(0, 1fr))`,
                            }}
                        >
                            {activeItem.dropdown.map((section) => (
                                <div key={section.title} className="home-secondary-mega-column">
                                    <h4>{section.title}</h4>
                                    <div className="home-secondary-mega-links">
                                        {section.items.map((subItem) => (
                                            <button
                                                type="button"
                                                key={subItem}
                                                onClick={() => {
                                                    setActiveDropdown(null);
                                                    navigate(
                                                        activeItem.id === "more-categories"
                                                            ? `/Categories/${encodeURIComponent(subItem)}`
                                                            : `/Categories/${encodeURIComponent(
                                                                  activeItem.label
                                                              )}?category=${encodeURIComponent(
                                                                  section.title
                                                              )}&subCategory=${encodeURIComponent(subItem)}`
                                                    );
                                                }}
                                            >
                                                <span>{subItem}</span>
                                                <ChevronRight size={12} />
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: "center", padding: "28px 0", color: "#687587", fontSize: "12px" }}>
                            No categories available
                        </div>
                    )}
                </div>
            )}

            {/* Browsing History Panel */}
            {browsingHistoryOpen && (
                <div className="home-secondary-history-panel">
                    <BrowsingHistoryTimeline onClose={() => onBrowsingHistoryClick?.()} />
                </div>
            )}

            {/* Mobile Categories Menu */}
            {mobileOpen && (
                <div className="home-secondary-mobile-menu">
                    {navItems.map((item) => {
                        const isHistoryActive = item.id === "history" && browsingHistoryOpen;
                        const isActive = activeDropdown === item.id || isHistoryActive;
                        const itemHasDropdown =
                            item.id !== "history" &&
                            item.id !== "deals" &&
                            item.id !== "vouchers" &&
                            item.id !== "appointment";

                        const itemData = menuCache[item.label];
                        const itemHasDynamicParents = Boolean(itemData && itemData.parents && itemData.parents.length > 0);

                        return (
                            <div key={item.id} className="home-secondary-mobile-item">
                                <button
                                    type="button"
                                    className={isActive ? "active" : ""}
                                    onClick={() => handleItemClick(item)}
                                >
                                    <span>
                                        <span className="home-secondary-mobile-icon">{item.icon}</span>
                                        {item.label}
                                    </span>
                                    {(itemHasDropdown || item.id === "history") && (
                                        <ChevronDown
                                            size={15}
                                            className={
                                                item.id === "history"
                                                    ? browsingHistoryOpen
                                                        ? "rotate"
                                                        : ""
                                                    : activeDropdown === item.id
                                                    ? "rotate"
                                                    : ""
                                            }
                                        />
                                    )}
                                </button>

                                {activeDropdown === item.id && itemHasDropdown && (
                                    <div className="home-secondary-mobile-dropdown">
                                        {loadingMenu && !itemData && !item.dropdown ? (
                                            <div
                                                style={{
                                                    textAlign: "center",
                                                    padding: "12px 0",
                                                    fontSize: "11px",
                                                    color: "#687587",
                                                }}
                                            >
                                                Loading categories…
                                            </div>
                                        ) : itemHasDynamicParents ? (
                                            itemData.parents.map((parent) => {
                                                const subs = itemData.subs[parent.name] || [];
                                                return (
                                                    <div key={parent._id} className="home-secondary-mobile-section">
                                                        <h4
                                                            onClick={() =>
                                                                handleParentClick(item.label, parent.name)
                                                            }
                                                        >
                                                            {parent.name}
                                                        </h4>
                                                        <div>
                                                            {subs.map((sub) => (
                                                                <button
                                                                    type="button"
                                                                    key={sub._id}
                                                                    onClick={() =>
                                                                        handleSubClick(
                                                                            item.label,
                                                                            parent.name,
                                                                            sub.name
                                                                        )
                                                                    }
                                                                >
                                                                    {sub.name}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : item.dropdown ? (
                                            item.dropdown.map((section) => (
                                                <div key={section.title} className="home-secondary-mobile-section">
                                                    <h4>{section.title}</h4>
                                                    <div>
                                                        {section.items.map((subItem) => (
                                                            <button
                                                                type="button"
                                                                key={subItem}
                                                                onClick={() => {
                                                                    setActiveDropdown(null);
                                                                    setMobileOpen(false);
                                                                    navigate(
                                                                        item.id === "more-categories"
                                                                            ? `/Categories/${encodeURIComponent(subItem)}`
                                                                            : `/Categories/${encodeURIComponent(
                                                                                  item.label
                                                                              )}?category=${encodeURIComponent(
                                                                                  section.title
                                                                              )}&subCategory=${encodeURIComponent(subItem)}`
                                                                    );
                                                                }}
                                                            >
                                                                {subItem}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))
                                        ) : (
                                            <div
                                                style={{
                                                    textAlign: "center",
                                                    padding: "10px 0",
                                                    fontSize: "10px",
                                                    color: "#888",
                                                }}
                                            >
                                                No categories available
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default HomeSecondaryNavbar;